import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Grid,
    Typography,
    Divider,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@material-ui/core";

const safeString = (v) => (typeof v === "string" ? v : "");
const safeBool = (v) => !!v;

const parseCommaList = (txt) =>
    txt
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

const formatToDateTimeLocal = (dateValue) => {
    if (!dateValue) return "";
    const d =
        typeof dateValue === "number"
            ? new Date(dateValue)
            : dateValue instanceof Date
                ? dateValue
                : new Date(dateValue);

    if (Number.isNaN(d.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const dateTimeLocalToISO = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString();
};

const isValidEmail = (email) => {
    const e = email.trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
};

const STATUS = {
    NOT_STARTED: "not_started",
    IN_PROGRESS: "in_progress",
    FINISHED: "finished"
};

export default function GameConfig({
    open,
    onClose,
    onSave,
    gameId,
    initialConfig = {},
    teamAName = "Equipo A",
    teamBName = "Equipo B"
}) {
    const initialState = useMemo(() => {
        const cfg = initialConfig || {};
        return {
            publishPublic: safeBool(cfg.publishPublic),
            gameDateISO: safeString(cfg.gameDateISO),
            referees: Array.isArray(cfg.referees) ? cfg.referees : [],
            coachEmailsTeamA: Array.isArray(cfg.coachEmailsTeamA) ? cfg.coachEmailsTeamA : [],
            coachEmailsTeamB: Array.isArray(cfg.coachEmailsTeamB) ? cfg.coachEmailsTeamB : [],
            notes: safeString(cfg.notes),

            // 👇 status + timestamps previos (si existen)
            status: safeString(cfg.status) || STATUS.NOT_STARTED,
            startedAtISO: safeString(cfg.startedAtISO),
            finishedAtISO: safeString(cfg.finishedAtISO),
            statusChangedAtISO: safeString(cfg.statusChangedAtISO)
        };
    }, [initialConfig]);

    const [publishPublic, setPublishPublic] = useState(false);
    const [gameDateLocal, setGameDateLocal] = useState("");
    const [refereesText, setRefereesText] = useState("");
    const [coachAEmailsText, setCoachAEmailsText] = useState("");
    const [coachBEmailsText, setCoachBEmailsText] = useState("");
    const [notes, setNotes] = useState("");

    // status state
    const [status, setStatus] = useState(STATUS.NOT_STARTED);
    const [startedAtISO, setStartedAtISO] = useState("");
    const [finishedAtISO, setFinishedAtISO] = useState("");
    const [statusChangedAtISO, setStatusChangedAtISO] = useState("");

    const [errors, setErrors] = useState({});

    useEffect(() => {
        setPublishPublic(initialState.publishPublic);
        setGameDateLocal(formatToDateTimeLocal(initialState.gameDateISO));
        setRefereesText((initialState.referees || []).join(", "));
        setCoachAEmailsText((initialState.coachEmailsTeamA || []).join(", "));
        setCoachBEmailsText((initialState.coachEmailsTeamB || []).join(", "));
        setNotes(initialState.notes || "");

        setStatus(initialState.status || STATUS.NOT_STARTED);
        setStartedAtISO(initialState.startedAtISO || "");
        setFinishedAtISO(initialState.finishedAtISO || "");
        setStatusChangedAtISO(initialState.statusChangedAtISO || "");

        setErrors({});
    }, [initialState, open]);

    const validate = () => {
        const nextErrors = {};

        if (gameDateLocal && !dateTimeLocalToISO(gameDateLocal)) {
            nextErrors.gameDateLocal = "Fecha/hora inválida";
        }

        const aList = parseCommaList(coachAEmailsText);
        const bList = parseCommaList(coachBEmailsText);

        const badA = aList.filter((e) => !isValidEmail(e));
        const badB = bList.filter((e) => !isValidEmail(e));

        if (badA.length) nextErrors.coachAEmailsText = `Correos inválidos: ${badA.join(", ")}`;
        if (badB.length) nextErrors.coachBEmailsText = `Correos inválidos: ${badB.join(", ")}`;

        // status permitido
        const allowed = [STATUS.NOT_STARTED, STATUS.IN_PROGRESS, STATUS.FINISHED];
        if (!allowed.includes(status)) nextErrors.status = "Status inválido";

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);

        // Guardamos timestamps solo si hay transición "hacia adelante"
        const nowISO = new Date().toISOString();
        setStatusChangedAtISO(nowISO);

        if (newStatus === STATUS.IN_PROGRESS && !startedAtISO) {
            setStartedAtISO(nowISO);
        }
        if (newStatus === STATUS.FINISHED && !finishedAtISO) {
            setFinishedAtISO(nowISO);
            // opcional: si alguien brinca directo a finished y no había startedAt
            if (!startedAtISO) setStartedAtISO(nowISO);
        }
    };

    const handleSave = () => {
        if (!validate()) return;

        const configToSave = {
            publishPublic,
            gameDateISO: dateTimeLocalToISO(gameDateLocal),
            referees: parseCommaList(refereesText),
            coachEmailsTeamA: parseCommaList(coachAEmailsText).map((e) => e.toLowerCase()),
            coachEmailsTeamB: parseCommaList(coachBEmailsText).map((e) => e.toLowerCase()),
            notes: notes.trim(),

            // 👇 nuevo
            status,
            statusChangedAtISO: statusChangedAtISO || "",
            startedAtISO: startedAtISO || "",
            finishedAtISO: finishedAtISO || ""
        };

        if (onSave) onSave(configToSave);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Configuración del juego</DialogTitle>

            <DialogContent>
                <Box mb={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={publishPublic}
                                onChange={(e) => setPublishPublic(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Publicar resultado en web pública"
                    />
                </Box>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    Link público :  <a target={"_blank"} href={"https://basketball.dashport.run/basketball/" + gameId}>{"https://basketball.dashport.run/basketball/" + gameId}</a>
                </Typography>



                <Grid container spacing={2}>
                    {/* 👇 STATUS */}
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!errors.status}>
                            <InputLabel>Status del juego</InputLabel>
                            <Select
                                value={status}
                                onChange={(e) => handleStatusChange(e.target.value)}
                            >
                                <MenuItem value={STATUS.NOT_STARTED}>No iniciado</MenuItem>
                                <MenuItem value={STATUS.IN_PROGRESS}>En proceso</MenuItem>
                                <MenuItem value={STATUS.FINISHED}>Finalizado</MenuItem>
                            </Select>
                            {errors.status ? (
                                <Typography variant="caption" color="error">
                                    {errors.status}
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="textSecondary">
                                    {status === STATUS.IN_PROGRESS && startedAtISO
                                        ? `Inicio registrado: ${new Date(startedAtISO).toLocaleString()}`
                                        : status === STATUS.FINISHED && finishedAtISO
                                            ? `Final registrado: ${new Date(finishedAtISO).toLocaleString()}`
                                            : " "}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Fecha y hora del juego"
                            type="datetime-local"
                            value={gameDateLocal}
                            onChange={(e) => setGameDateLocal(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.gameDateLocal}
                            helperText={errors.gameDateLocal || " "}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Árbitros (separa por comas)"
                            value={refereesText}
                            onChange={(e) => setRefereesText(e.target.value)}
                            fullWidth
                            placeholder="Ej: Juan Pérez, María López"
                        />
                    </Grid>
                </Grid>

                <Box my={2}>
                    <Divider />
                </Box>

                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    Acceso para coaches por correo
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                    Escribe uno o varios correos separados por comas.
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={`Coaches - ${teamAName} (correos separados por comas)`}
                            value={coachAEmailsText}
                            onChange={(e) => setCoachAEmailsText(e.target.value)}
                            fullWidth
                            placeholder="coachA@correo.com, asistente@correo.com"
                            error={!!errors.coachAEmailsText}
                            helperText={errors.coachAEmailsText || " "}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label={`Coaches - ${teamBName} (correos separados por comas)`}
                            value={coachBEmailsText}
                            onChange={(e) => setCoachBEmailsText(e.target.value)}
                            fullWidth
                            placeholder="coachB@correo.com"
                            error={!!errors.coachBEmailsText}
                            helperText={errors.coachBEmailsText || " "}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Notas internas"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Cualquier info adicional..."
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" color="primary" onClick={handleSave}>
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
